<template>
  <div class="container">
    <div class="title-top">{{appName}} Privacy Terms & Conditions</div>
    <div class="article-card">
      <h2 class="title-secondary">{{appName}} User Data Protection Policy</h2>

      <p>
        While you are using our mobile application services, we will collect and manage your personal information accordingly.
        This policy clearly defines how our platform gathers, utilises and discloses user data throughout your service experience.
      </p>
      <br>
      <p class="notice-highlight">
        To fully understand how we safeguard your personal information and legitimate rights, please read this policy carefully.
      </p>
      <br>

      <p class="catalog-heading">This policy covers the following main sections:</p>
      <p>1. Collection and practical use of your personal data</p>
      <p>2. Rules for sharing, transferring and public release of personal data</p>
      <p>3. Usage rules for Cookies, SDK and other technical tools</p>
      <p>4. Comprehensive security protection for personal data</p>
      <p>5. Self-service management of your personal data</p>
      <p>6. Data processing rules for underage users</p>
      <p>7. Retention and storage rules of personal data</p>
      <p>8. Revision rules and notification mechanism of this policy</p>
      <p>9. Contact channels for consultation and feedback</p>
      <br>

      <h3 class="main-heading">Introduction</h3>
      <p>
        {{appName}} regards user information security as our top priority. We maintain strict confidentiality for all collected user data
        and have established a complete information protection system, aiming to deliver reliable and secure services to every user.
      </p>
      <br>

      <h3 class="main-heading">1. Collection and Use of Personal Data</h3>
      <p>
        Personal data refers to any information recorded electronically or in other forms that can identify a specific individual
        or reflect personal activities. It includes name, date of birth, biometric features, residential address, contact details,
        chat records, account passwords, asset information, physical health status, transaction records and other related content.
      </p>
      <br>
      <p>
        Sensitive personal data refers to information that may pose threats to personal and property safety, damage personal reputation
        or lead to unfair treatment once leaked, illegally transferred or misused. This includes biometric data, bank account information,
        chat content, asset status, location traces, health records, transaction data and personal information of users aged 14 and under.
      </p>
      <br>

      <p class="text-emphasis">You acknowledge and agree to the following clauses:</p>
      <p>
        1. We provide a wide range of products and services to meet your diverse needs. Different service functions will collect different types
        and ranges of personal data. The specific collection standards shall be subject to the actual functions you use.
      </p>
      <p>
        2. We keep optimizing products and technical capabilities to improve user experience. When launching new functions or upgrading existing services,
        we may collect new types of data or adjust the way we use existing information. We will explain relevant rules via policy updates,
        pop-up reminders or page prompts, and we will only collect data after obtaining your explicit consent.
        Feel free to contact us if you have any questions or suggestions.
      </p>
      <p>
        3. Data collected from one service function may be applied to other services on our platform, such as personalized content push,
        security alerts and user portrait analysis. If we intend to use your data beyond the original scope, we will inform you again
        and acquire your approval in advance.
      </p>
      <br>

      <p class="text-warning">Important Tip:</p>
      <p>
        Our platform provides user recommendation services. The nickname, age, gender, constellation, occupation, workplace,
        hometown, usual activity areas, personal signature, avatar, photos and public posts you publish
        may be displayed and recommended to other users on the platform.
      </p>
      <br>

      <h3 class="main-heading">Service Functions and Collected Data</h3>
      <p>
        In accordance with relevant national regulations, {{appName}} is categorized as a social communication application.
        Its core service is online instant messaging including text, images, voice and video.
        The personal data collected by each function are listed below:
      </p>
      <br>
      <p>
        1. Account Registration: To complete registration, you need to provide your email address, nickname, gender, country,
        birth date and avatar. These data are used to create your account and enable social functions. Meanwhile, we collect device data
        such as device model, system version, unique device identifier, network information, application list and sensor data
        to ensure stable operation of our services.
      </p>
      <p>
        2. Chat Service: Basic chat functions do not require extra personal data. We collect your account information to maintain
        normal service operation and account security. When sending pictures, voice messages or initiating video calls,
        the app will request access to your album and microphone. You may reject these permissions without affecting basic chat services.
      </p>
      <p>
        3. Video Call: This function requires camera and microphone permissions. You cannot use video call services normally without authorization.
      </p>
      <p>
        4. Browse User Moments: Viewing moments content does not require any personal data or extra system permissions.
      </p>
      <p>
        5. Visit User Homepage: You can view other users' photos and videos without special permissions.
        Initiating video calls on the homepage requires camera and microphone access.
      </p>
      <p>
        6. Personal Profile Editing: When changing your avatar, the app will request album permission.
        Refusing this permission will not affect basic application functions.
      </p>
      <p>
        7. Payment Service: Payment functions are provided by our third-party payment partners. We will not obtain your payment-related data.
        Disabling relevant permissions will only disable payment functions, while other basic services remain available.
      </p>
      <p>
        8. Message Reminders: We will apply for auto-start permission to push message notifications in a timely manner.
        You can turn off this permission anytime in your mobile system settings.
      </p>
      <br>

      <p>
        Permissions for camera, album and microphone are disabled by default. We will pop up an independent authorization window
        when you use related functions. You can enable or disable permissions freely in device settings. Closing permissions means
        you withdraw authorization, and we will stop collecting corresponding data and suspend related services.
        Closing permissions will not affect data collected previously with your approval.
      </p>
      <br>

      <p>
        We may send service notifications, security verification messages and user experience surveys to your reserved email.
        If you publish other people's personal information on our platform, you must obtain legal authorization in advance.
      </p>
      <br>

      <p>
        Your submitted information will be used during your use of services. After you cancel your account or stop using certain functions,
        we will cease data usage and complete data anonymization or deletion in accordance with legal requirements.
      </p>
      <br>

      <h3 class="main-heading">Usage of Collected Data</h3>
      <p>
        We only use your data for the purposes stated in this policy. If we need to use data for other purposes, we will obtain your prior consent.
        When acquiring data from third parties, we will require them to collect information legally with user consent and guarantee the legality of data sources.
        We apply the same strict security protection to externally obtained data as our own user data.
      </p>
      <br>

      <p class="text-emphasis">Exceptions to Authorization Requirements</p>
      <p>
        Under the following circumstances, we do not need your consent to collect and use personal data,
        and we may not respond to your requests for modification, deletion or withdrawal of consent:
      </p>
      <p>1. To fulfill statutory obligations required by laws and regulations</p>
      <p>2. Matters concerning national security and national defense</p>
      <p>3. For public safety, public health and general public interests</p>
      <p>4. For criminal investigation, litigation, trial and judgment enforcement</p>
      <p>5. To protect individuals' legitimate rights when it is impractical to obtain consent</p>
      <p>6. Information that you voluntarily disclose to the public</p>
      <p>7. Data collected from legally public information channels</p>
      <br>

      <p>
        We process collected data through desensitization, statistical analysis and algorithm training to optimize products and services.
        Processed anonymous data cannot identify specific individuals, so no additional notification or consent is required.
        If we stop platform operation, we will immediately stop data collection, release service termination notices,
        and delete or anonymize all stored personal data.
      </p>
      <br>

      <h3 class="main-heading">Information You Share Publicly</h3>
      <p>
        You can share texts, images and other content with other users via our platform, which may contain your personal or sensitive data.
        Please think carefully before publishing private information. You can delete your published content at any time,
        but please note that such content may be saved by other users or third parties beyond our management scope.
      </p>
      <br>

      <h3 class="main-heading">2. Sharing, Transfer and Public Release of Personal Data</h3>
      <h4 class="sub-heading">Data Sharing</h4>
      <p>
        We will not share your personal data with external companies, organizations or individuals except for the following cases:
      </p>
      <p>1. Share data after obtaining your explicit authorization;</p>
      <p>2. Share data as required by laws, regulations or judicial authorities;</p>
      <p>3. Share necessary data with affiliated enterprises to provide joint services and protect user security.
        We will apply for your re-authorization if sensitive data is involved or data usage purposes are changed;</p>
      <p>4. Provide necessary data to authorized service partners, who can only use data to complete assigned work.</p>
      <br>

      <h4 class="sub-heading">Data Transfer</h4>
      <p>
        We will not transfer your personal data to any external party unless:
      </p>
      <p>1. You have given explicit consent to data transfer;</p>
      <p>2. In case of merger, acquisition or bankruptcy reorganization. The new data holder must abide by this policy;
        otherwise, they need to obtain your re-authorization again.</p>
      <br>

      <h4 class="sub-heading">Public Release</h4>
      <p>1. Public release based on your explicit consent or active operation;</p>
      <p>2. Release relevant information in accordance with laws and platform rules to safeguard public interests
        when you violate relevant regulations.</p>
      <br>

      <h3 class="main-heading">3. Use of Cookies and SDK Technologies</h3>
      <h4 class="sub-heading">Cookies</h4>
      <p>
        We store small data files called Cookies on your device to ensure normal service operation,
        optimize your usage experience and recommend content you may be interested in. You can clear Cookies via browser settings,
        but some functions relying on Cookies may become unavailable afterwards.
      </p>
      <br>

      <h4 class="sub-heading">Third-Party SDK</h4>
      <p>
        Some functions rely on third-party SDK services. Third-party providers will independently apply for permissions and collect data.
        The main integrated SDKs include: Google Firebase Push SDK, Third-party Payment SDK, NetEase Yunxin SDK,
        Shengwang SDK, AppsFlyer SDK, Facebook SDK, Huawei Push SDK.
        Rejecting relevant permissions will only disable corresponding third-party functions, and will not affect other normal functions of {{appName}}.
      </p>
      <br>

      <h3 class="main-heading">4. Personal Data Security Protection</h3>
      <p>
        We strictly comply with laws and industry specifications and adopt multiple measures to protect your information security:
      </p>
      <p>
        1. We use encryption, desensitization and access control technologies to prevent unauthorized access, leakage, damage and loss of personal data.
        We also organize regular training to enhance employees' awareness of data protection.
      </p>
      <p>
        2. We have built a full life-cycle data security management system. Once a data security incident occurs,
        we will activate emergency plans immediately and notify users via official announcements or message pushes.
      </p>
      <p>
        3. We only collect necessary data and retain your information only within the period required for service purposes,
        unless the retention period is extended in accordance with laws.
      </p>
      <p>
        4. The internet cannot be fully secure. Please do not send sensitive personal information through unofficial channels.
        If you find your account or password is leaked, please contact our customer service promptly.
      </p>
      <br>

      <h3 class="main-heading">5. Manage Your Personal Data</h3>
      <h4 class="sub-heading">Data Query</h4>
      <p>
        Within the scope permitted by law, you have the right to view your personal data.
        You can check and modify information via <strong>[Mine - Top Right - Settings]</strong>.
        Click the avatar in personal center to edit nickname, avatar, birth date and personal introduction.
      </p>
      <br>

      <h4 class="sub-heading">Data Correction</h4>
      <p>
        If you find errors in your personal data, you can submit a correction application through the above entry.
      </p>
      <br>

      <h4 class="sub-heading">Data Deletion</h4>
      <p>
        You can delete part of your personal data independently. You can also apply for data deletion in the following situations:
      </p>
      <p>1. Our data processing violates relevant laws and regulations;</p>
      <p>2. Data is collected and used without your explicit consent;</p>
      <p>3. We seriously violate the agreed terms;</p>
      <p>4. You stop using services or cancel your account;</p>
      <p>5. We permanently stop providing services for you.</p>
      <br>

      <h4 class="sub-heading">Adjust Authorization Scope</h4>
      <p>
        For additional data collection permissions, you can enable or cancel authorization by yourself,
        or contact our customer service for assistance.
      </p>
      <br>

      <h3 class="main-heading">6. Processing of Underage Users' Information</h3>
      <p class="text-forbid">This application prohibits users under 18 years of age from registering and using.</p>
      <p>
        Our services are not targeted at users under 18. We will not actively collect or trade minors' personal information.
        If we accidentally obtain data from minor users, we will delete it immediately.
        If we adjust relevant rules in the future, we will obtain verifiable consent from guardians before collecting minors' information.
      </p>
      <br>

      <h3 class="main-heading">7. Personal Data Storage Rules</h3>
      <p>
        Our service servers are deployed in multiple regions worldwide. Your personal data may be stored and processed on servers outside your region.
        You agree to cross-regional transmission and storage of your data for the purposes specified in this policy.
      </p>
      <br>

      <h3 class="main-heading">8. Policy Revision and Notification</h3>
      <p>
        We reserve the right to revise this privacy policy. You can check the latest version anytime via the link on our official website.
        We will release official announcements for major content changes. Your continued use of services after policy updates
        means you accept the revised terms. If you disagree with the updated content, please stop using our services.
      </p>
      <br>

      <h3 class="main-heading">9. Contact Us</h3>
      <p>
        If you have any questions about this privacy policy or data protection matters,
        please send emails to: <span class="link-email">{{appName}}@{{appName}}.site</span>
      </p>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {};
  },
  created() {}
};
</script>

<style scoped>
/* 全新独立样式：极简商务风，与历史版本完全区分 */
.container {
  width: 100%;
  padding: 24px 16px;
  box-sizing: border-box;
  background-color: #ffffff;
  min-height: 100vh;
  font-family: Arial, Helvetica, sans-serif;
}

.title-top {
  font-size: 28px;
  color: #212529;
  text-align: center;
  font-weight: bold;
  margin-bottom: 26px;
  letter-spacing: 0.5px;
}

.article-card {
  max-width: 920px;
  margin: 0 auto;
  padding: 36px 40px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background-color: #fdfdfd;
  font-size: 15px;
  line-height: 1.9;
  color: #495057;
}

.title-secondary {
  font-size: 21px;
  color: #0056b3;
  text-align: center;
  margin: 0 0 20px 0;
  font-weight: 600;
}

.notice-highlight {
  background-color: #fff3cd;
  color: #856404;
  padding: 12px 16px;
  border-radius: 3px;
  font-weight: 500;
}

.catalog-heading {
  font-size: 16px;
  color: #343a40;
  font-weight: 600;
  margin: 12px 0 6px 0;
}

.main-heading {
  font-size: 18px;
  color: #0056b3;
  font-weight: 600;
  margin: 30px 0 12px 0;
}

.sub-heading {
  font-size: 16px;
  color: #343a40;
  font-weight: 600;
  margin: 16px 0 8px 0;
}

.text-emphasis {
  font-weight: 600;
  color: #dc3545;
}

.text-warning {
  font-weight: 600;
  color: #dc3545;
}

.text-forbid {
  font-size: 16px;
  font-weight: 600;
  color: #dc3545;
}

.link-email {
  color: #0056b3;
  text-decoration: none;
  font-weight: 500;
}
</style>